// Thin wrappers around the nexus-server REST endpoints the cockpit uses.
// These are VPS-scoped — `apiVps` auto-prefixes `/api/vps/<active_vps_id>`
// so the UI host can route the reverse-proxy to the right tailnet target.
//
// The auth layer is responsible for ensuring `active_vps_id` is set before
// any of these are called (it is, by the route guard in App.tsx).

import { apiVps } from "./client";
import type {
  Account,
  AccountAlgoLink,
  Algo,
  BacktestRequest,
  BacktestResponse,
  CreateAccountRequest,
  JournalAggregates,
  JournalAlgoCompare,
  JournalSource,
  KillScope,
  KillStatus,
  LinkAlgoRequest,
  MarketWidgets,
  PatchAccountRequest,
  PredictiveOverview,
  RithmicConnectRequest,
  TradovateConnectRequest,
  TodayPnL,
  Trade,
  TradeJournalEntry,
  PortfolioMetrics,
  PortfolioRiskSnapshot,
  SleeveAlert,
  SleevePerformance,
  RecentActivity,
  CorrelationEntry,
  MarginInfo,
  BarDto,
} from "./types";

// Build a query string from a param bag, dropping undefined/null/empty.
function qs(params: Record<string, string | number | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

export const venues = {
  list: () => apiVps<import("./types").VenueInfo[]>("/venues"),
};

/** Wire shape of GET/PATCH /api/risk_gates/{broker_id}/{account_id}. */
export interface RiskGateConfig {
  broker_id: string;
  account_id: number;
  max_contracts?: number | null;
  max_drawdown_usd?: number | null;
  daily_loss_cap_usd?: number | null;
  max_orders_per_minute?: number | null;
  max_concurrent_direction?: number | null;
}

export const riskGates = {
  /** The BINDING dispatch-time caps (risk_gates row) for an account. */
  get: (broker: string, accountId: number) =>
    apiVps<RiskGateConfig>(`/risk_gates/${encodeURIComponent(broker)}/${accountId}`),
};


export const accounts = {
  list: () => apiVps<Account[]>("/accounts"),
  create: (body: CreateAccountRequest) =>
    apiVps<number>("/accounts", { method: "POST", body }),
  get: (id: number) => apiVps<Account>(`/accounts/${id}`),
  patch: (id: number, body: PatchAccountRequest) =>
    apiVps<Account>(`/accounts/${id}`, { method: "PATCH", body }),
  status: (id: number) => apiVps<{ status: string; error?: string | null }>(
    `/accounts/${id}/status`,
  ),
  todayPnl: (id: number) => apiVps<TodayPnL>(`/accounts/${id}/today-pnl`),
  totalPnl: (id: number) => apiVps<{ pnl: number }>(`/accounts/${id}/pnl`),
  trades: (id: number) => apiVps<Trade[]>(`/accounts/${id}/trades`),
  // Algos LINKED to this account (not the global roster) — so each account's
  // desk shows only its own sleeves.
  algos: (id: number) => apiVps<Algo[]>(`/accounts/${id}/algos`),
  rithmicConnect: (id: number, body: RithmicConnectRequest) =>
    apiVps<Account>(`/accounts/${id}/rithmic/connect`, {
      method: "POST",
      body,
    }),
  /**
   * Re-attempt the Rithmic handshake using credentials already saved
   * on the account row. No body required — server reads
   * rithmic_config_json + retries handshake_and_register. Use this
   * after a transient failure (Rithmic maintenance window,
   * R-Trader Pro briefly held the session) instead of asking the
   * operator to re-enter credentials.
   */
  rithmicRetry: (id: number) =>
    apiVps<Account>(`/accounts/${id}/rithmic/retry`, { method: "POST" }),
  tradovateConnect: (id: number, body: TradovateConnectRequest) =>
    apiVps<Account>(`/accounts/${id}/tradovate/connect`, {
      method: "POST",
      body,
    }),
  tradovateRetry: (id: number) =>
    apiVps<Account>(`/accounts/${id}/tradovate/retry`, { method: "POST" }),
  tradovateDisconnect: (id: number) =>
    apiVps<Account>(`/accounts/${id}/tradovate/disconnect`, { method: "POST" }),
  flatten: (id: number) =>
    apiVps<{ ok: boolean }>(`/accounts/${id}/flatten`, { method: "POST" }),
  retire: (id: number) =>
    apiVps<{ account_id: number; retired: boolean; message: string }>(`/accounts/${id}/retire`, { method: "POST" }),
  testOrder: (id: number) =>
    apiVps<unknown>(`/accounts/${id}/test-order`, { method: "POST" }),
  links: (id: number) => apiVps<AccountAlgoLink[]>(`/accounts/${id}/links`),
  linkAlgo: (id: number, body: LinkAlgoRequest) =>
    apiVps<AccountAlgoLink>(`/accounts/${id}/links`, {
      method: "POST",
      body,
    }),
  patchLink: (
    id: number,
    algoId: number,
    body: { active?: boolean; contracts?: number; daily_loss_limit?: number | null },
  ) =>
    apiVps<unknown>(`/accounts/${id}/links/${algoId}`, {
      method: "PATCH",
      body,
    }),
  unlink: (id: number, algoId: number) =>
    apiVps<unknown>(`/accounts/${id}/links/${algoId}`, { method: "DELETE" }),
};

export const algos = {
  list: () => apiVps<Algo[]>("/algos"),
  start: (id: number) => apiVps<unknown>("/algos/" + id + "/start", { method: "POST" }),
  stop: (id: number) => apiVps<unknown>("/algos/" + id + "/stop", { method: "POST" }),
  metrics: (id: number) => apiVps<Record<string, unknown>>("/algos/" + id + "/metrics"),
};

export interface JournalListParams {
  // "both"/all → leave source undefined (server returns every source).
  source?: JournalSource;
  since?: string; // ISO date bound on entry_ts (inclusive)
  until?: string; // ISO date bound on entry_ts (exclusive day-end)
  algo_id?: number;
  status?: string;
  exit_reason?: string;
  before_id?: number; // cursor: rows with id < before_id (200/page when set)
  limit?: number; // default 5000, cap 5000 when no before_id
  /** Scope to a specific account. When omitted, returns all accounts. */
  account_id?: number;
}

export const journal = {
  list: (p: JournalListParams = {}) =>
    apiVps<TradeJournalEntry[]>(`/journal${qs({ ...p })}`),
  aggregates: (p: Omit<JournalListParams, "before_id" | "limit"> = {}) =>
    apiVps<JournalAggregates>(`/journal/aggregates${qs({ ...p })}`),
  compare: (algoId: number) =>
    apiVps<JournalAlgoCompare>(`/journal/compare/${algoId}`),
};

// ── Portfolio Command Center ────────────────────────────────────────────

export const portfolio = {
  // Engine wraps metrics/activity in object keys; sleeves/alerts also
  // come wrapped, so unwrap in .then() for a consistent flat SPA shape.
  metrics: (account_id?: number) =>
    apiVps<{ metrics: PortfolioMetrics }>(`/portfolio/metrics${qs({ account_id })}`).then(
      (r) => r.metrics,
    ),
  sleeves: (account_id?: number) =>
    apiVps<{ sleeves: SleevePerformance[] }>(`/portfolio/sleeves${qs({ account_id })}`).then(
      (r) => r.sleeves,
    ),
  risk: (account_id?: number) =>
    apiVps<PortfolioRiskSnapshot>(`/portfolio/risk${qs({ account_id })}`),
  activity: (account_id?: number) =>
    apiVps<{ activity: RecentActivity }>(`/portfolio/activity${qs({ account_id })}`).then(
      (r) => r.activity,
    ),
  alerts: (account_id?: number) =>
    apiVps<{ alerts: SleeveAlert[] }>(`/portfolio/alerts${qs({ account_id })}`).then(
      (r) => r.alerts,
    ),
  correlationMatrix: (account_id?: number) =>
    apiVps<CorrelationEntry[]>(`/portfolio/correlation${qs({ account_id })}`).then(
      (r) => (Array.isArray(r) ? r : (r as { matrix: CorrelationEntry[] }).matrix ?? []),
    ),
  marketWidgets: () => apiVps<MarketWidgets>("/market/widgets"),
};

export const kill = {
  status: () => apiVps<KillStatus>("/kill"),
  engage: (scope: KillScope, account_id?: number, reason?: string) =>
    apiVps<KillStatus>("/kill", {
      method: "POST",
      body: { scope, account_id, reason },
    }),
  clear: (account_id?: number) =>
    apiVps<KillStatus>("/kill/clear", {
      method: "POST",
      body: account_id !== undefined ? { account_id } : {},
    }),
};

// ── Backtest API ──
export const backtest = {
  run: (body: BacktestRequest, signal?: AbortSignal) =>
    apiVps<BacktestResponse>("/backtest/run", { method: "POST", body, signal }),
};

// ── Predictive indicators ──
export const predictive = {
  overview: () => apiVps<PredictiveOverview>("/predictive/overview"),
};

export const market = {
  margins: () => apiVps<Record<string, MarginInfo>>("/market/margins"),
};

/** Algo-specific data for sleeve insight page. */
export const algoData = {
  liveBars: (id: number, limit?: number) =>
    apiVps<BarDto[]>("/algos/" + id + "/live-bars?limit=" + (limit ?? 200)),
};

// ── Structured positions (PositionsPanel v2) ────────────────────────────────
// Server-assembled open positions (sim + live) with marks, UPL, risk,
// MAE/MFE, and exit ETA. Replaces the old eval-note regex scraping.

export interface ExitEta {
  /** "HH:MM ET" of the scheduled exit minute. */
  et_time: string;
  /** Minutes from now until the scheduled exit (negative = overdue). */
  minutes_until: number;
}

export interface PositionRow {
  algo_id: number | null;
  account_id: number;
  account_name: string;
  /** "sim" | "live" */
  source: string;
  sleeve: string;
  market: string;
  symbol: string;
  side: "long" | "short";
  qty: number;
  entry: number;
  entry_ts: string | null;
  mark: number | null;
  mark_ts: string | null;
  upl: number | null;
  stop: number | null;
  stop_trailing: boolean;
  stop_missing: boolean;
  risk_usd: number | null;
  tp: number | null;
  mae_points: number | null;
  mfe_points: number | null;
  exit_eta: ExitEta | null;
  /** Sleeve paused by operator — dispatch blocked until resume. */
  paused: boolean;
}

export interface PositionsResponse {
  sim: PositionRow[];
  live: PositionRow[];
  generated_at: string;
}

export interface CloseSimResult {
  closed: boolean;
  algo_id: number;
  account_id: number;
  exit_price: number;
  pnl: number;
  exit_reason: string;
}

export interface CloseLiveResult {
  submitted: boolean;
  paused: boolean;
  symbol: string;
  algo_id: number;
  account_id: number;
  message: string;
}

export interface ResumePauseResult {
  algo_id: number;
  account_id: number;
  resumed: boolean;
}

export const positions = {
  list: () => apiVps<PositionsResponse>("/positions"),
  closeSim: (algoId: number, accountId: number) =>
    apiVps<CloseSimResult>("/positions/close-sim", {
      method: "POST",
      body: { algo_id: algoId, account_id: accountId },
    }),
  closeLive: (algoId: number, accountId: number, symbol: string) =>
    apiVps<CloseLiveResult>("/positions/close-live", {
      method: "POST",
      body: { algo_id: algoId, account_id: accountId, symbol },
    }),
  resume: (algoId: number, accountId: number) =>
    apiVps<ResumePauseResult>("/positions/resume", {
      method: "POST",
      body: { algo_id: algoId, account_id: accountId },
    }),
};

// ── Upcoming events (auto-pull for UpcomingTradesCard) ──
export interface UpcomingEvent {
  date: string;
  event: string;
  time_et?: string | null;
  impact: string;
  source: string;
}

export const events = {
  upcoming: () => apiVps<UpcomingEvent[]>("/events/upcoming"),
};
